// NavBar.tsx
import React, { useMemo, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MenuItem } from "../types/types";
import { useAuth } from "../contexts/AuthContext";
import { buildReadableSet, filterMenuByRoles } from "./MenuFilter";
import { MENU_DATA } from "./MenuData";
import classes from "./NavBar.module.css";
import { NavLink as MantineNavLink } from "@mantine/core";
import { IconGauge } from "@tabler/icons-react";
import { useDynamicNavbarHeight } from "../utils/useDynamicNavbarHeight";
import axiosInstance from "../axiosInstance";
import { FRONTEND_VERSION } from "../constants/version";

type MenuNodeProps = { item: MenuItem; depth?: number };

const isGroup = (item: MenuItem) =>
  Array.isArray(item.sub) && item.sub.length > 0;

const MenuNode: React.FC<MenuNodeProps> = ({ item, depth = 1 }) => {
  const location = useLocation();
  const getDepthClass = (depth: number) => {
    if (depth === 1) return classes.depth1;
    if (depth === 2) return classes.depth2;
    return "";
  };
  if (isGroup(item)) {
    // 부모: 링크 없음, 펼침/접힘만
    return (
      <MantineNavLink
        label={item.name}
        leftSection={item.icon ? <item.icon /> : null}
        childrenOffset={28}
        defaultOpened
        classNames={{
          root: classes.navLinkRoot,
          collapse: getDepthClass(depth), // ✅ 여기!
        }}
      >
        {item.sub!.map((child) => (
          <MenuNode
            key={child.apiUrl ?? child.name}
            item={child}
            depth={depth + 1}
          />
        ))}
      </MantineNavLink>
    );
  }

  // 리프: 내부 라우터 이동 (새로고침 없이)
  if (item.apiUrl) {
    return (
      <MantineNavLink
        component={Link}
        to={item.apiUrl}
        label={item.name}
        leftSection={item.icon ? <item.icon /> : null}
        className={classes.navLinkRoot}
        active={location.pathname === item.apiUrl}
      />
    );
  }

  // apiUrl도 sub도 없으면 안전하게 텍스트만
  return (
    <MantineNavLink
      label={item.name}
      leftSection={item.icon ? <item.icon /> : null}
      disabled
    />
  );
};

const NavBar: React.FC = () => {
  const [isDev, setIsDev] = useState(false);
  const isLocal =
    process.env.REACT_APP_IS_DEV === "true" ||
    (typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"));
  useEffect(() => {
    axiosInstance.get("/api/v1/common/info").then((res) => {
      if (res.data.data === "dev") {
        setIsDev(true);
      }
    });
  }, []);
  const is237 =
    typeof window !== "undefined" &&
    window.location.hostname === "192.168.0.237";
  const { roles } = useAuth(); // roles: Role[]
  const readable = useMemo(() => buildReadableSet(roles ?? []), [roles]);
  const filteredMenu = useMemo(
    () => filterMenuByRoles(MENU_DATA, readable),
    [readable],
  );

  // 동적 사이드바 높이 제어 (최소 높이 600px)
  const { navbarHeight } = useDynamicNavbarHeight({ minHeight: 600 });

  return (
    <nav
      className={classes.navbar}
      style={{ height: navbarHeight }}
      data-navbar
    >
      <div
        className={`${classes.header} ${
          isLocal
            ? classes.headerLocal
            : isDev || is237
              ? classes.headerDev
              : ""
        }`}
      >
        {`MES${window.location.hostname === "192.168.0.237" ? "(237)" : ""}`}
      </div>

      <div className={classes.menuContainer}>
        {filteredMenu.map((m) => (
          <MenuNode key={m.apiUrl ?? m.name} item={m} />
        ))}
      </div>

      <div className={classes.footer}>
        <div>Copyright ©2026 Company.,Ltd.All Rights Reserved.</div>
        <div className={classes.footerVersion}>Version {FRONTEND_VERSION}</div>
      </div>
    </nav>
  );
};

export default NavBar;
